import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Save, Plus, Trash2, X, Shield } from 'lucide-react';
import * as HeroIconsOutline from '@heroicons/react/24/outline';

const ProcessStepModal = ({ isOpen, onClose, onSubmit, step, isEditing }) => {
    const [formData, setFormData] = useState({
        number: '',
        title: '',
        description: '',
        is_active: true
    });

    useEffect(() => {
        if (step) {
            setFormData(step);
        } else {
            setFormData({
                number: '',
                title: '',
                description: '',
                is_active: true
            });
        }
    }, [step, isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl transform transition-all">
                <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-slate-800">
                        {isEditing ? 'Edit Process Step' : 'Add New Step'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Step Number</label>
                        <input
                            type="text"
                            value={formData.number}
                            onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., 01"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Step Title</label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Business Profile Review"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                        <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Comprehensive analysis of operations..."
                        />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                        <input
                            type="checkbox"
                            id="is_active"
                            checked={formData.is_active !== false}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 border-gray-300"
                        />
                        <label htmlFor="is_active" className="text-sm font-medium text-slate-700">Set as Active</label>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-600 font-medium hover:bg-slate-50 rounded-lg transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => onSubmit(formData)}
                        className="px-6 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
                    >
                        {isEditing ? 'Save Changes' : 'Add Step'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const RatingBehindScenesPage = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sectionData, setSectionData] = useState({
        section_tag: '',
        title: '',
        description: '',
        monitoring_title: '',
        monitoring_text: '',
        process_steps: []
    });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/admin/rating-behind-scenes');
            if (response.data.success && response.data.data) {
                const data = response.data.data;
                const parsedSteps = typeof data.process_steps === 'string'
                    ? JSON.parse(data.process_steps)
                    : (data.process_steps || []);

                setSectionData({
                    section_tag: data.section_tag || '',
                    title: data.title || '',
                    description: data.description || '',
                    monitoring_title: data.monitoring_title || '',
                    monitoring_text: data.monitoring_text || '',
                    process_steps: parsedSteps
                });
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
            setLoading(false);
        }
    };

    const handleSettingsSave = async () => {
        try {
            setSaving(true);
            await axios.put('/api/admin/rating-behind-scenes', {
                ...sectionData,
                process_steps: sectionData.process_steps
            });
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving data:', error);
            toast.error('Failed to save data');
        } finally {
            setSaving(false);
        }
    };

    const saveProcessSteps = async (newSteps) => {
        try {
            await axios.put('/api/admin/rating-behind-scenes', {
                ...sectionData,
                process_steps: newSteps
            });
            setSectionData(prev => ({ ...prev, process_steps: newSteps }));
            return true;
        } catch (error) {
            console.error('Error updating process steps:', error);
            toast.error('Failed to update process steps');
            return false;
        }
    };

    const handleModalSubmit = async (stepData) => {
        let newSteps = [...sectionData.process_steps];
        if (editingIndex !== null) {
            newSteps[editingIndex] = stepData;
            toast.success('Step updated');
        } else {
            newSteps.push(stepData);
            toast.success('Step added');
        }

        const success = await saveProcessSteps(newSteps);
        if (success) {
            setIsModalOpen(false);
            setEditingIndex(null);
        }
    };

    const handleDelete = async (index) => {
        if (window.confirm('Are you sure you want to delete this step?')) {
            const newSteps = sectionData.process_steps.filter((_, i) => i !== index);
            await saveProcessSteps(newSteps);
            toast.success('Step deleted');
        }
    };

    if (loading) return <div className="p-6">Loading...</div>;

    return (
        <div className="space-y-6 pb-12">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Behind the Scenes Section</h1>
                    <p className="text-slate-500 mt-1">Manage "How the Rating Was Determined" section content</p>
                </div>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Tag</label>
                        <input
                            type="text"
                            value={sectionData.section_tag}
                            onChange={(e) => setSectionData({ ...sectionData, section_tag: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Behind the Scenes"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Section Title</label>
                        <input
                            type="text"
                            value={sectionData.title}
                            onChange={(e) => setSectionData({ ...sectionData, title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., How the Rating Was Determined"
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Description/Intro</label>
                    <textarea
                        value={sectionData.description}
                        onChange={(e) => setSectionData({ ...sectionData, description: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                    />
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-md transition-all font-medium"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Continuous Monitoring Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Continuous Monitoring Card</h2>
                <div className="grid grid-cols-1 gap-4 mb-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Card Title</label>
                        <input
                            type="text"
                            value={sectionData.monitoring_title}
                            onChange={(e) => setSectionData({ ...sectionData, monitoring_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="e.g., Continuous Monitoring"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-1">Card Text</label>
                        <textarea
                            value={sectionData.monitoring_text}
                            onChange={(e) => setSectionData({ ...sectionData, monitoring_text: e.target.value })}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 resize-none"
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-xl hover:bg-primary-700 disabled:opacity-50 shadow-md transition-all font-medium"
                    >
                        <Save size={18} />
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Process Steps Grid */}
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Rating Process Steps</h2>
                        <p className="text-sm text-slate-500">Manage the step-by-step rating process</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingIndex(null);
                            setIsModalOpen(true);
                        }}
                        className="flex items-center gap-2 bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 shadow-lg hover:shadow-xl transition-all font-semibold"
                    >
                        <Plus size={20} /> Add Step
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sectionData.process_steps.map((step, index) => (
                        <div
                            key={index}
                            className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-all group"
                        >
                            <div className="h-1.5 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                            <div className="p-5">
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-sm">
                                        <span className="text-2xl font-bold">{step.number}</span>
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => {
                                                setEditingIndex(index);
                                                setIsModalOpen(true);
                                            }}
                                            className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-primary-600 transition-colors"
                                            title="Edit"
                                        >
                                            <HeroIconsOutline.PencilIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(index)}
                                            className="p-2 hover:bg-slate-50 rounded-lg text-slate-400 hover:text-red-600 transition-colors"
                                            title="Delete"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between mb-4">
                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${step.is_active !== false ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>
                                        {step.is_active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-lg font-bold text-slate-800">{step.title}</h3>
                                    <p className="text-slate-600 text-sm leading-relaxed">
                                        {step.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}

                    {sectionData.process_steps.length === 0 && (
                        <div className="col-span-full py-12 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                                <Shield className="h-8 w-8 text-slate-300" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800 mb-1">No process steps added</h3>
                            <button
                                onClick={() => {
                                    setEditingIndex(null);
                                    setIsModalOpen(true);
                                }}
                                className="text-primary-600 font-medium hover:text-primary-700"
                            >
                                + Add First Step
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <ProcessStepModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleModalSubmit}
                step={editingIndex !== null ? sectionData.process_steps[editingIndex] : null}
                isEditing={editingIndex !== null}
            />
        </div>
    );
};

export default RatingBehindScenesPage;
